import { ObjectAnimatorBase } from "./ObjectAnimatorBase.js";

export class TextAnimator extends ObjectAnimatorBase {
    static styleId = 'text-animator-styles';
    static styles = `
        .text-overlay {
            position: absolute;
            color: white;
            pointer-events: none;
            padding: 0.5em 1em;
            border-radius: 0.25em;
            opacity: 0; /* Start invisible for animations */
            max-width: 90%; 
            word-wrap: break-word; 
            box-sizing: border-box;
        }

        .text-position-top-left { top: 5%; left: 5%; text-align: left; }
        .text-position-top-center { top: 5%; left: 50%; transform: translateX(-50%); text-align: center; }
        .text-position-top-right { top: 5%; right: 5%; text-align: right; }
        .text-position-center-left { top: 50%; left: 5%; transform: translateY(-50%); text-align: left; }
        .text-position-center { top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; }
        .text-position-center-right { top: 50%; right: 5%; transform: translateY(-50%); text-align: right; }
        .text-position-bottom-left { bottom: 5%; left: 5%; text-align: left; }
        .text-position-bottom-center { bottom: 5%; left: 50%; transform: translateX(-50%); text-align: center; }
        .text-position-bottom-right { bottom: 5%; right: 5%; text-align: right; }

        /* FIX: Use a CSS variable set by the previewer for responsive font sizing. Fallback to vw. */
        .text-size-small { font-size: calc(var(--preview-font-size-base, 1.8vw) * 0.67); } /* Approx 1.2vw */
        .text-size-medium { font-size: var(--preview-font-size-base, 1.8vw); }
        .text-size-large { font-size: calc(var(--preview-font-size-base, 1.8vw) * 1.39); } /* Approx 2.5vw */
        .text-size-xlarge { font-size: calc(var(--preview-font-size-base, 1.8vw) * 1.94); } /* Approx 3.5vw */

        /* Keyframe Animations */
        @keyframes anim-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes anim-fade-out { from { opacity: 1; } to { opacity: 0; } }
        @keyframes anim-slide-up-in { from { opacity: 0; transform: translateY(50%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes anim-slide-down-out { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(50%); } }
        @keyframes anim-pop-in { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
        @keyframes anim-pop-out { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.5); } }
        @keyframes anim-stomp-in { from { opacity: 0; transform: scale(1.5); } to { opacity: 1; transform: scale(1); } }
        @keyframes anim-burst-in { 0% { opacity: 0; transform: scale(0); } 60% { opacity: 1; transform: scale(1.1); } 100% { transform: scale(1); } }
    `;

    constructor(canvasElement, appClient) {
        super(canvasElement, appClient);
        TextAnimator.injectStyles(TextAnimator.styles, TextAnimator.styleId);
    }

    render(clip, currentTime) {
        let textElement = this.activeElements.get(clip._id);
        if (!textElement) {
            textElement = document.createElement('div');
            textElement.className = 'text-overlay';
            this.canvas.appendChild(textElement);
            this.activeElements.set(clip._id, textElement);
        }

        const { position, animation, styling, text } = clip;
        const preset = position?.preset || 'bottom-center';
        const fontSize = styling?.fontSize || 'medium';
        const inEffect = animation?.inEffect || 'fade';
        const outEffect = animation?.outEffect || 'fade';
        const outDuration = animation?.outDuration || 0.5;

        // Apply positioning and styling
        textElement.className = `text-overlay text-position-${preset} text-size-${fontSize}`;
        textElement.style.color = styling?.color || '#FFFFFF';
        textElement.style.backgroundColor = styling?.backgroundColor || 'rgba(0,0,0,0.4)';
        textElement.style.fontWeight = styling?.fontWeight || 'bold';
        textElement.style.textShadow = (styling?.textShadow !== false) ? '2px 2px 8px rgba(0,0,0,0.8)' : 'none';

        // Handle Animation
        const clipAge = currentTime - clip.startTime;
        const timeLeft = (clip.startTime + clip.duration) - currentTime;

        // Reset animation before applying new state
        textElement.style.animation = 'none';

        if (inEffect === 'typewriter') {
            const CHARACTERS_PER_SECOND = 25;
            const textContent = text || '';
            const effectiveInDuration = textContent.length / CHARACTERS_PER_SECOND;

            if (clipAge < effectiveInDuration) {
                const progress = clipAge / effectiveInDuration;
                const charsToShow = Math.floor(progress * textContent.length);
                textElement.textContent = textContent.substring(0, charsToShow);
                textElement.style.opacity = 1;
            } else {
                textElement.textContent = textContent;
                textElement.style.opacity = 1;
            }
        } else {
            const inDuration = animation?.inDuration || 0.5;
            textElement.textContent = text || '';
            if (clipAge < inDuration) {
                textElement.style.animation = `anim-${inEffect}-in ${inDuration}s ease-out forwards`;
            } else {
                textElement.style.opacity = 1;
            }
        }

        // Handle out-animation (applies to all effects)
        if (timeLeft < outDuration) {
            if (inEffect === 'typewriter') {
                textElement.textContent = text || '';
            }
            textElement.style.animation = `anim-${outEffect}-out ${outDuration}s ease-in forwards`;
        }
    }
}